import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { NavService } from '../../../_services/_nav.service';
import { ListaService } from '../../../_services/lista.service';
import { ProductoService } from '../../../_services/producto.service';
import { Producto } from '../../../_models/producto';
import { ListaItem } from '../../../_models/lista-item';
import { ProductoTroncalProductoAgrupado } from '../../../_models/producto-troncal-producto-agrupado';

@Component({
  selector: 'app-productos-troncal-productos',
  templateUrl: 'productos-troncal-productos.component.html',
  styleUrls: [ 'productos-troncal-productos.component.css' ]
})
export class ProductosTroncalProductosComponent implements OnInit {

  @Input() productos: ProductoTroncalProductoAgrupado[] = [];
  @Output() productosChange = new EventEmitter<ProductoTroncalProductoAgrupado[]>();
  @Input() verDetalle = false;

  carInformacionInicial = true;
  mostrarAgregar = false;
  submitted = false;

  lisTiposProducto: SelectItem[] = [];
  lisProductosDisponibles: SelectItem[] = [];
  lisProductosTodos: Producto[] = [];

  formulario: FormGroup;
  columnas: any[];
  busqueda: string;
  @ViewChild('dt') tabla: Table;

  private eliminados: ProductoTroncalProductoAgrupado[] = [];

  constructor( private lisServicio: ListaService,
               private productoServicio: ProductoService,
               private navServicio: NavService,
               private conServicio: ConfirmationService,
               private fb: FormBuilder ) {
  }

  ngOnInit(): void {
    this.columnas = [
      { field: 'codigoProducto', header: this.navServicio.buscarMensaje( 'PRODUCTOSTRONCAL.LBL_CODIGO' ), sort: true },
      { field: 'nombreProducto', header: this.navServicio.buscarMensaje( 'PRODUCTOSTRONCAL.LBL_PRODUCTO' ), sort: true },
      { field: 'tipoProducto', header: this.navServicio.buscarMensaje( 'PRODUCTOSTRONCAL.LBL_TIPO' ), sort: false }
    ];

    if ( this.verDetalle ) {
      this.carInformacionInicial = false;
      return;
    }

    this.columnas.push( { field: 'acciones', header: this.navServicio.buscarMensaje( 'LBL_ACCIONES' ), sort: false } );

    this.inicializarFormulario();

    forkJoin(
      this.lisServicio.getMasterDetails( 'ListasTiposProductosBancarios' ),
      this.productoServicio.listadoHabilitados()
    ).subscribe( ( [ tiposProducto, productos ] ) => {
      this.lisTiposProducto = tiposProducto.map( ( s: ListaItem ) => ( { label: s.nombre, value: s.idLista } ) );
      this.lisProductosTodos = productos;
      this.recalcularDisponibles();
      this.carInformacionInicial = false;
    } );
  }

  inicializarFormulario() {
    this.formulario = this.fb.group( {
      idTipoProducto: [ null, [ Validators.required ] ],
      idProducto: [ null, [ Validators.required ] ]
    } );
    this.formulario.get( 'idTipoProducto' ).valueChanges.subscribe( () => {
      this.formulario.get( 'idProducto' ).setValue( null );
      this.recalcularDisponibles();
    } );
    this.recalcularDisponibles();
  }

  /**
   * El dropdown Producto solo lista productos del Tipo de producto seleccionado
   * (Producto.idTipoProductoBancario) y excluye los ya agregados a la tabla.
   * Sin Tipo de producto elegido, la lista queda vacía (el template muestra el aviso).
   */
  recalcularDisponibles() {
    const idTipoProducto = this.formulario.get( 'idTipoProducto' ).value;
    if ( !idTipoProducto ) {
      this.lisProductosDisponibles = [];
      return;
    }
    this.lisProductosDisponibles = this.lisProductosTodos
      .filter( p => p.idTipoProductoBancario === idTipoProducto )
      .filter( p => !this.productos.find( x => x.idProducto === p.idProducto ) )
      .map( p => ( { label: p.nombre, value: p.idProducto } ) );
  }

  mostrarPanelAgregar() {
    this.submitted = false;
    this.inicializarFormulario();
    this.mostrarAgregar = true;
  }

  agregar() {
    this.submitted = true;
    if ( !this.formulario.valid ) {
      return;
    }
    const idTipoProducto = this.formulario.value.idTipoProducto;
    const idProducto = this.formulario.value.idProducto;

    /*
     * Si el producto ya había sido eliminado en esta misma sesión de edición,
     * se reutiliza ese registro original (mismo idProductoTroncalProductoAgrupado)
     * en vez de crear uno nuevo con id null — evita guardar dos filas para el
     * mismo producto (una inactiva con id real, otra activa con id null).
     */
    const eliminadoPrevio = this.eliminados.find( x => x.idProducto === idProducto );
    if ( eliminadoPrevio ) {
      this.eliminados = this.eliminados.filter( x => x !== eliminadoPrevio );
      this.productos = [ ...this.productos, { ...eliminadoPrevio, indicadorHabilitado: true } ];
      this.productosChange.emit( this.productos );
      this.recalcularDisponibles();
      this.mostrarAgregar = false;
      return;
    }

    const tipoProducto = this.lisTiposProducto.find( x => x.value === idTipoProducto );
    const producto = this.lisProductosTodos.find( x => x.idProducto === idProducto );

    const fila: ProductoTroncalProductoAgrupado = {
      idProductoTroncalProductoAgrupado: null,
      idProductoTroncal: null,
      idTipoProducto,
      tipoProducto: tipoProducto ? tipoProducto.label : null,
      idProducto,
      codigoProducto: producto ? producto.codigo : null,
      nombreProducto: producto ? producto.nombre : null,
      indicadorHabilitado: true,
      auditoriaFecha: null,
      auditoriaUsuario: null
    };

    this.productos = [ ...this.productos, fila ];
    this.productosChange.emit( this.productos );
    this.recalcularDisponibles();
    this.mostrarAgregar = false;
  }

  cancelarAgregar() {
    this.mostrarAgregar = false;
  }

  eliminar( row: ProductoTroncalProductoAgrupado ) {
    this.conServicio.confirm( {
      key: 'eliminarProductoTroncal',
      icon: 'fa fa-trash',
      header: this.navServicio.buscarMensaje( 'PRODUCTOSTRONCAL.LBL_ELIMINARPRODUCTO' ),
      message: this.navServicio.buscarMensaje( 'PRODUCTOSTRONCAL.MSG_CONFIRMARELIMINARPRODUCTO' ),
      accept: () => {
        if ( row.idProductoTroncalProductoAgrupado ) {
          this.eliminados.push( { ...row, indicadorHabilitado: false } );
        }
        this.productos = this.productos.filter( x => x !== row );
        this.productosChange.emit( this.productos );
        this.recalcularDisponibles();
      }
    } );
  }

  obtenerEliminados(): ProductoTroncalProductoAgrupado[] {
    return this.eliminados;
  }

  get f() {
    return this.formulario.controls;
  }

}
